/**
 * Vendor API Client
 * Handles communication with the live vendor portal backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://vendor-portal-backend.yellowforest-ecf59216.westus.azurecontainerapps.io/api';

export interface VendorRegistrationData {
  companyName: string;
  vendorName: string;
  email: string;
  phone?: string;
  businessType?: string;
  federalTaxId?: string;
  legalBusinessName?: string;
  website?: string;
  businessAddress?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country?: string;
  };
  foundedYear?: number;
  employeeCount?: string;
  annualRevenue?: string;
  primaryIndustry?: string;
  businessStructure?: string;
  ownershipType?: string;
  headquartersLocation?: string;
  serviceAreas?: string;
  primaryContactName?: string;
  primaryContactTitle?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  servicesOffered?: string[];
  certifications?: string[];
  insurance?: {
    generalLiability?: string;
    professionalLiability?: string;
    workersCompensation?: string;
  };
  diversityStatus?: string;
  securityClearance?: string;
  backgroundCheckStatus?: string;
  notes?: string;
  status?: string;
}

export interface VendorResponse {
  success: boolean;
  vendor?: any;
  vendorId?: string;
  pdf?: {
    generated: boolean;
    filename: string;
    size: number;
  };
  sharepoint?: {
    configured: boolean;
    pending_sync: boolean;
  };
  message?: string;
  error?: string;
}

export interface VendorListResponse {
  success: boolean;
  data: any[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    pages: number;
    currentPage: number;
  };
}

export interface VendorStatsResponse {
  success: boolean;
  stats: {
    total_vendors: number;
    pending_vendors: number;
    approved_vendors: number;
    completion_rate: string;
  };
  timestamp: string;
}

class VendorApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/vendors`;
  }

  /**
   * Register a new vendor
   */
  async registerVendor(vendorData: VendorRegistrationData): Promise<VendorResponse> {
    const response = await fetch(`${this.baseUrl}/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(vendorData)
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to register vendor');
    }

    return result;
  }

  /**
   * Get all vendors with pagination
   */
  async getVendors(options: {
    page?: number;
    limit?: number;
    status?: string;
  } = {}): Promise<VendorListResponse> {
    const { page = 1, limit = 20, status } = options;
    const offset = (page - 1) * limit;
    
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    if (status) params.append('status', status);

    const response = await fetch(`${this.baseUrl}?${params}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch vendors');
    }

    return result;
  }

  /**
   * Get vendor by ID
   */
  async getVendorById(vendorId: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/${vendorId}`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch vendor');
    }

    return result.data;
  }

  /**
   * Update vendor status
   */
  async updateVendorStatus(vendorId: string, status: string, notes?: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/${vendorId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status, notes })
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to update vendor status');
    }

    return result;
  }

  /**
   * Generate PDF for vendor
   */
  async generateVendorPDF(vendorId: string, format: 'download' | 'base64' = 'download'): Promise<Blob | any> {
    const response = await fetch(`${this.baseUrl}/${vendorId}/generate-pdf?format=${format}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate PDF');
    }

    if (format === 'download') {
      return await response.blob();
    } else {
      return await response.json();
    }
  }

  /**
   * Download vendor PDF
   */
  async downloadVendorPDF(vendorId: string, filename?: string): Promise<void> {
    const blob = await this.generateVendorPDF(vendorId, 'download');
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `vendor-${vendorId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Create test vendor (for testing)
   */
  async createTestVendor(): Promise<VendorResponse> {
    const response = await fetch(`${this.baseUrl}/test-registration`, {
      method: 'POST',
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to create test vendor');
    }

    return result;
  }

  /**
   * Get vendor statistics
   */
  async getVendorStats(): Promise<VendorStatsResponse> {
    const response = await fetch(`${this.baseUrl}/stats/overview`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to fetch vendor statistics');
    }

    return result;
  }

  /**
   * Check backend health
   */
  async checkHealth(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/health`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error('Backend health check failed');
    }

    return result;
  }

  /**
   * Create document record for vendor
   */
  async createDocument(vendorId: string, documentData: {
    document_type: string;
    document_name: string;
    file_name?: string;
    file_path?: string;
    file_size?: number;
    mime_type?: string;
    status?: string;
    docusign_envelope_id?: string;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/${vendorId}/documents`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(documentData)
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || 'Failed to create document record');
    }

    return result;
  }
}

// PDF API Client
class PDFApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/pdf`;
  }

  /**
   * Generate PDF from vendor data
   */
  async generatePDF(vendorData: VendorRegistrationData, format: 'download' | 'base64' = 'download'): Promise<Blob | any> {
    const response = await fetch(`${this.baseUrl}/generate?format=${format}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ vendorData })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate PDF');
    }

    if (format === 'download') {
      return await response.blob();
    } else {
      return await response.json();
    }
  }

  /**
   * Get sample vendor data for testing
   */
  async getSampleData(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/sample-data`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error('Failed to fetch sample data');
    }

    return result;
  }

  /**
   * Download test PDF
   */
  async downloadTestPDF(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/test`);
    
    if (!response.ok) {
      throw new Error('Failed to download test PDF');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'test-vendor.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// SharePoint API Client
class SharePointApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/sharepoint`;
  }

  /**
   * Check SharePoint health and configuration
   */
  async checkHealth(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/health`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error('SharePoint health check failed');
    }

    return result;
  }

  /**
   * Get SharePoint configuration
   */
  async getConfig(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/config`);
    const result = await response.json();

    if (!response.ok) {
      throw new Error('Failed to fetch SharePoint configuration');
    }

    return result;
  }
}

// Export singleton instances
export const vendorApi = new VendorApiClient();
export const pdfApi = new PDFApiClient();
export const sharepointApi = new SharePointApiClient(); 